import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getLogs, LOG_ACTIONS } from '../services/logService';
import styles from '../styles/adminLogsStyles';

const AdminLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Estados para los filtros
  const [filters, setFilters] = useState({
    action: '',
    resourceType: '',
    startDate: null,
    endDate: null,
    userEmail: ''
  });

  // Cargar logs al iniciar el componente
  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async (customFilters = null) => {
    try {
      setLoading(true);
      
      // Usar filtros personalizados o los del estado
      const filtersToApply = customFilters || filters;
      
      // Preparar filtros para la consulta
      const queryFilters = {};
      
      if (filtersToApply.action) {
        queryFilters.action = filtersToApply.action;
      }
      
      if (filtersToApply.resourceType) {
        queryFilters.resourceType = filtersToApply.resourceType;
      }
      
      if (filtersToApply.startDate) {
        queryFilters.startDate = filtersToApply.startDate;
      }
      
      if (filtersToApply.endDate) {
        // Ajustar la fecha final para incluir todo el día
        const endDate = new Date(filtersToApply.endDate);
        endDate.setHours(23, 59, 59, 999);
        queryFilters.endDate = endDate;
      }
      
      // Obtener logs desde el servicio
      const logsData = await getLogs(queryFilters, 500);
      
      // Filtrar por email si se especificó (hacemos este filtrado en el cliente)
      let filteredLogs = logsData;
      if (filtersToApply.userEmail && filtersToApply.userEmail.trim() !== '') {
        const emailFilter = filtersToApply.userEmail.toLowerCase().trim();
        filteredLogs = logsData.filter(log => 
          log.userEmail && log.userEmail.toLowerCase().includes(emailFilter)
        );
      }
      
      setLogs(filteredLogs);
      setError(null);
    } catch (error) {
      console.error('Error al cargar logs:', error);
      setError('Error al cargar los registros de actividad. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (name, date) => {
    setFilters(prev => ({
      ...prev,
      [name]: date
    }));
  };

  const handleApplyFilters = () => {
    fetchLogs();
  };

  const handleResetFilters = () => {
    setFilters({
      action: '',
      resourceType: '',
      startDate: null,
      endDate: null,
      userEmail: ''
    });
    fetchLogs({
      action: '',
      resourceType: '',
      startDate: null,
      endDate: null,
      userEmail: ''
    });
  };

  // Función para formatear los detalles del log
  const formatLogDetails = (log) => {
    if (!log.details) return '-';
    
    try {
      if (log.resourceType === 'acta') {
        if (log.action === LOG_ACTIONS.APPROVE || log.action === LOG_ACTIONS.REJECT) {
          return `${log.details.actaTitle || 'Sin título'} (${log.details.previousStatus || 'N/A'} → ${log.details.newStatus || 'N/A'})`;
        }
        return `Acta #${log.details.numeroActa || ''} - ${log.details.tipoActa || log.details.actaTitle || ''}`;
      }
      return JSON.stringify(log.details);
    } catch (e) {
      return '-';
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={500} color="primary">
          Registro de Actividades
        </Typography>
        <Box>
          <Tooltip title="Refrescar datos">
            <IconButton onClick={() => fetchLogs()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Mostrar/ocultar filtros">
            <IconButton onClick={() => setShowFilters(!showFilters)} color="primary">
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {showFilters && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Acción</InputLabel>
                <Select
                  name="action"
                  value={filters.action}
                  label="Acción"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value={LOG_ACTIONS.CREATE}>Crear</MenuItem>
                  <MenuItem value={LOG_ACTIONS.UPDATE}>Editar</MenuItem>
                  <MenuItem value={LOG_ACTIONS.DELETE}>Eliminar</MenuItem>
                  <MenuItem value={LOG_ACTIONS.VIEW}>Visualizar</MenuItem>
                  <MenuItem value={LOG_ACTIONS.APPROVE}>Aprobar</MenuItem>
                  <MenuItem value={LOG_ACTIONS.REJECT}>Rechazar</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de recurso</InputLabel>
                <Select
                  name="resourceType"
                  value={filters.resourceType}
                  label="Tipo de recurso"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="acta">Acta</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Email de usuario"
                name="userEmail"
                value={filters.userEmail}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <DatePicker
                  label="Fecha inicio"
                  value={filters.startDate}
                  onChange={(date) => handleDateChange('startDate', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <DatePicker
                  label="Fecha fin"
                  value={filters.endDate}
                  onChange={(date) => handleDateChange('endDate', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleApplyFilters}
                  size="small"
                >
                  Aplicar filtros
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={handleResetFilters}
                  size="small"
                >
                  Limpiar filtros
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Fecha y hora</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Detalles</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No hay registros de actividad
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {format(log.timestamp, 'dd/MM/yyyy HH:mm:ss', { locale: es })}
                    </TableCell>
                    <TableCell>{log.userEmail}</TableCell>
                    <TableCell>
                      <Box sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        bgcolor: 
                          log.action === LOG_ACTIONS.CREATE ? '#e6f7ed' :
                          log.action === LOG_ACTIONS.UPDATE ? '#e6f0f7' :
                          log.action === LOG_ACTIONS.DELETE ? '#f7e6e6' :
                          '#f5f5f5',
                        color: 
                          log.action === LOG_ACTIONS.CREATE ? '#0d904f' :
                          log.action === LOG_ACTIONS.UPDATE ? '#0d5990' :
                          log.action === LOG_ACTIONS.DELETE ? '#900d0d' :
                          '#666666',
                      }}>
                        {log.action}
                      </Box>
                    </TableCell>
                    <TableCell>{log.resourceType}</TableCell>
                    <TableCell>{formatLogDetails(log)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default AdminLogs;